import React, { useMemo, useState } from 'react'
import Style from '../viewSummary.module.css'
import { formatCount, percent } from '../summaryFormat'
import { entityIcon } from '../../../SharedComponents/EntityIcon/entityIcon'

const PIPELINE_COLUMNS = [
    { key: 'requestReceived', label: 'Request received', help: 'Picked up from the sending system and queued' },
    { key: 'inProcess', label: 'In process', help: 'Being sent to the receiving system' },
    { key: 'responseReceived', label: 'Response received', help: 'Receiving system has responded' },
    { key: 'responseTransferred', label: 'Response transferred', help: 'Response handed back to the sending system' }
]

const SORTABLE = {
    entityName: (a, b) => a.entityName.localeCompare(b.entityName),
    error: (a, b) => a.error - b.error,
    completed: (a, b) => a.completed - b.completed,
    total: (a, b) => a.total - b.total
}

const healthOf = (row) => {
    if (row.total === 0) return { tone: 'neutral', label: 'No activity' }
    if (row.error > 0) return { tone: 'danger', label: 'Needs attention' }
    if (row.inFlight > 0) return { tone: 'info', label: 'In progress' }
    return { tone: 'success', label: 'Healthy' }
}

const DistributionBar = ({ row }) => {
    if (row.total === 0) {
        return <span className={Style.bar} aria-hidden="true" />
    }
    const segments = [
        { tone: Style.barSuccess, value: row.completed, label: 'completed' },
        { tone: Style.barInfo, value: row.inFlight, label: 'in progress' },
        { tone: Style.barDanger, value: row.error, label: 'failed' }
    ].filter((segment) => segment.value > 0)

    const summary = segments.map((segment) => `${percent(segment.value, row.total)}% ${segment.label}`).join(', ')

    return (
        <span className={Style.bar} role="img" aria-label={summary} title={summary}>
            {segments.map((segment) => (
                <span
                    key={segment.label}
                    className={segment.tone}
                    style={{ width: `${(segment.value / row.total) * 100}%` }}
                />
            ))}
        </span>
    )
}

const CountCell = ({ value, tone, onOpen, label }) => (
    <td className={Style.countCell}>
        {value > 0 ? (
            <button
                type="button"
                className={[Style.countLink, tone ? Style[`countLink_${tone}`] : ''].join(' ')}
                onClick={onOpen}
                aria-label={`View ${formatCount(value)} ${label} records`}
            >
                {formatCount(value)}
            </button>
        ) : (
            <span className={Style.countZero}>0</span>
        )}
    </td>
)

const SortHeader = ({ column, label, sort, onSort, className, rowSpan }) => {
    const isActive = sort.key === column
    const ariaSort = isActive ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
    return (
        <th className={className} aria-sort={ariaSort} rowSpan={rowSpan}>
            <button type="button" className={Style.sortButton} onClick={() => onSort(column)}>
                {label}
                <i
                    className={[
                        isActive ? (sort.dir === 'asc' ? 'uil-arrow-up' : 'uil-arrow-down') : 'uil-sort',
                        Style.sortIcon,
                        isActive ? Style.sortIconActive : ''
                    ].join(' ')}
                    aria-hidden="true"
                />
            </button>
        </th>
    )
}

const SkeletonRows = () =>
    [0, 1, 2, 3].map((index) => (
        <tr key={index} className={Style.skeletonRow}>
            <td><span className={Style.skeleton} style={{ width: '70%' }} /></td>
            <td><span className={Style.skeleton} style={{ width: 90 }} /></td>
            {[0, 1, 2, 3, 4, 5, 6].map((cell) => (
                <td key={cell} className={Style.countCell}><span className={Style.skeleton} style={{ width: 24 }} /></td>
            ))}
        </tr>
    ))

const EntitySummaryTable = ({ entities, totals, isLoading, loadError, onRetry, onOpen, rangeLabel }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [failedOnly, setFailedOnly] = useState(false)
    const [sort, setSort] = useState({ key: null, dir: 'desc' })

    const failedCount = useMemo(() => entities.filter((row) => row.error > 0).length, [entities])

    const visible = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        let rows = entities.filter(
            (row) => (!failedOnly || row.error > 0) && (term === '' || row.entityName.toLowerCase().includes(term))
        )
        if (sort.key) {
            const compare = SORTABLE[sort.key]
            rows = rows.slice().sort((a, b) => (sort.dir === 'asc' ? compare(a, b) : compare(b, a)))
        }
        return rows
    }, [entities, searchTerm, failedOnly, sort])

    const onSort = (key) =>
        setSort((current) =>
            current.key === key
                ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: key === 'entityName' ? 'asc' : 'desc' }
        )

    const hasRows = !isLoading && !loadError && entities.length > 0

    const renderBody = () => {
        if (isLoading) {
            return <SkeletonRows />
        }
        if (loadError) {
            return (
                <tr>
                    <td colSpan={9} className={Style.stateCell}>
                        <i className={`uil-exclamation-octagon ${Style.stateIconError}`} aria-hidden="true" />
                        <p className={Style.stateText}>{loadError}</p>
                        <button type="button" className={Style.primaryButton} onClick={onRetry}>Retry</button>
                    </td>
                </tr>
            )
        }
        if (entities.length === 0) {
            return (
                <tr>
                    <td colSpan={9} className={Style.stateCell}>
                        <i className={`uil-chart-bar ${Style.stateIcon}`} aria-hidden="true" />
                        <p className={Style.stateText}>No sync activity {rangeLabel ? `for ${rangeLabel.toLowerCase()}` : 'yet'}</p>
                        <p className={Style.stateHint}>Try a wider date range or switch the sync direction.</p>
                    </td>
                </tr>
            )
        }
        if (visible.length === 0) {
            return (
                <tr>
                    <td colSpan={9} className={Style.stateCell}>
                        <i className={`uil-search ${Style.stateIcon}`} aria-hidden="true" />
                        <p className={Style.stateText}>No entities match your filters</p>
                    </td>
                </tr>
            )
        }
        return visible.map((row) => {
            const health = healthOf(row)
            const open = (status) => () => onOpen(status, row.entityId)
            return (
                <tr key={row.entityId || row.entityName}>
                    <th scope="row" className={Style.entityTh}>
                        <div className={Style.entityCell}>
                            <span className={Style.entityIcon}>
                                <i className={entityIcon(row.entityName)} aria-hidden="true" />
                            </span>
                            <span className={Style.entityMeta}>
                                <span className={Style.entityName} title={row.entityName}>{row.entityName}</span>
                                <DistributionBar row={row} />
                            </span>
                        </div>
                    </th>
                    <td>
                        <span className={[Style.pill, Style[`pill_${health.tone}`]].join(' ')}>
                            <span className={Style.pillDot} aria-hidden="true" />
                            {health.label}
                        </span>
                    </td>
                    {PIPELINE_COLUMNS.map((column) => (
                        <CountCell
                            key={column.key}
                            value={row[column.key]}
                            label={column.label.toLowerCase()}
                            onOpen={open(column.key)}
                        />
                    ))}
                    <CountCell value={row.error} tone="danger" label="failed" onOpen={open('error')} />
                    <CountCell value={row.completed} tone="success" label="completed" onOpen={open('completed')} />
                    <CountCell value={row.total} tone="strong" label="total" onOpen={open('total')} />
                </tr>
            )
        })
    }

    return (
        <section className={Style.card} aria-labelledby="entity-summary-title">
            <header className={Style.cardHeader}>
                <div className={Style.cardTitle}>
                    <h2 id="entity-summary-title" className={Style.cardTitleText}>Entity breakdown</h2>
                    {hasRows ? <span className={Style.countChip}>{entities.length}</span> : null}
                </div>
                {hasRows ? (
                    <div className={Style.toolbar}>
                        <button
                            type="button"
                            className={[Style.filterChip, failedOnly ? Style.filterChipActive : ''].join(' ')}
                            aria-pressed={failedOnly}
                            disabled={failedCount === 0}
                            onClick={() => setFailedOnly(!failedOnly)}
                        >
                            <i className="uil-exclamation-triangle" aria-hidden="true" />
                            Failed only{failedCount > 0 ? ` (${failedCount})` : ''}
                        </button>
                        <div className={Style.search}>
                            <i className={`uil-search ${Style.searchIcon}`} aria-hidden="true" />
                            <input
                                type="search"
                                className={Style.searchInput}
                                placeholder="Search entities"
                                aria-label="Search entities"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                        </div>
                    </div>
                ) : null}
            </header>

            <div className={Style.tableScroll}>
                <table className={Style.table}>
                    <thead>
                        <tr>
                            <SortHeader column="entityName" label="Entity" sort={sort} onSort={onSort} className={Style.thEntity} rowSpan={2} />
                            <th rowSpan={2} className={Style.thLeft}>Status</th>
                            <th colSpan={4} className={Style.thGroup}>In progress</th>
                            <SortHeader column="error" label="Failed" sort={sort} onSort={onSort} rowSpan={2} />
                            <SortHeader column="completed" label="Completed" sort={sort} onSort={onSort} rowSpan={2} />
                            <SortHeader column="total" label="Total" sort={sort} onSort={onSort} rowSpan={2} />
                        </tr>
                        <tr>
                            {PIPELINE_COLUMNS.map((column) => (
                                <th key={column.key} className={Style.thSub} title={column.help}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{renderBody()}</tbody>
                    {hasRows && visible.length > 1 && !searchTerm && !failedOnly ? (
                        <tfoot>
                            <tr>
                                <th scope="row" colSpan={2} className={Style.footLabel}>All entities</th>
                                {PIPELINE_COLUMNS.map((column) => (
                                    <CountCell
                                        key={column.key}
                                        value={totals[column.key]}
                                        label={column.label.toLowerCase()}
                                        onOpen={() => onOpen(column.key)}
                                    />
                                ))}
                                <CountCell value={totals.error} tone="danger" label="failed" onOpen={() => onOpen('error')} />
                                <CountCell value={totals.completed} tone="success" label="completed" onOpen={() => onOpen('completed')} />
                                <CountCell value={totals.total} tone="strong" label="total" onOpen={() => onOpen('total')} />
                            </tr>
                        </tfoot>
                    ) : null}
                </table>
            </div>
        </section>
    )
}

export default EntitySummaryTable
